import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { captureDashboardCreated } from "@/lib/insights/custom-insights-analytics";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import {
  createDuplicatedDashboard,
  invalidateDashboardsListQuery,
  resolveDashboardFromCacheOrFetch,
} from "./utils";
import { createCustomDashboard } from "@tracify/shared";
import { getCustomInsightsApiRequestOptions } from "@/lib/insights/custom-insights-api-client";
import {
  mapApiDashboardToDashboard,
  mapDashboardToApiUpsertInput,
  normalizeDashboardForUpsertInput,
} from "@/lib/insights/dashboard-mappers";

export function useDuplicateDashboard() {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { selectedOrganisation } = useSelectedStores();

  return useMutation({
    mutationFn: async (dashboardId: string) => {
      if (!selectedOrganisation) {
        throw new Error("Organisation ID is required");
      }

      const sourceDashboard = await resolveDashboardFromCacheOrFetch(
        queryClient,
        selectedOrganisation,
        dashboardId
      );

      if (!sourceDashboard) {
        return null;
      }

      const duplicatedDashboard = createDuplicatedDashboard(sourceDashboard);
      const createdDashboard = await createCustomDashboard(
        getCustomInsightsApiRequestOptions(selectedOrganisation),
        mapDashboardToApiUpsertInput(
          normalizeDashboardForUpsertInput(duplicatedDashboard)
        )
      );

      return mapApiDashboardToDashboard(createdDashboard);
    },
    onSuccess: async (newDashboard) => {
      if (!newDashboard) {
        return;
      }

      captureDashboardCreated(posthog, {
        dashboardId: newDashboard.id,
        source: "duplicate",
      });

      await invalidateDashboardsListQuery(queryClient, selectedOrganisation);
    },
  });
}
